import { makeObservable, observable } from 'mobx'

import { required } from '../common/objectUtils'
import { UnprocessedValueError } from '../common/tsUtils'

import { StoreAccountModel } from '../types/accountProps'
import { integrationTypeNames, StoreIntegrationType } from '../types/integrationTypeUtils'

import { WbAccountModel } from '../server/mpsklad_core/Models/WbAccountModel'
import { IntegrationType } from '../server/mpsklad_core/Entity/IntegrationType'
import { OzonAccountModel } from '../server/mpsklad_core/Models/OzonAccountModel'
import { MoySkladAccountModel } from '../server/mpsklad_core/Models/MoySkladAccountModel'
import { YandexMarketAccountModel } from '../server/mpsklad_core/Models/YandexMarketAccountModel'

export class SyncStore {
  @observable
  ozonAccounts: OzonAccountModel[]

  @observable
  wbAccounts: WbAccountModel[]

  @observable
  yandexMarketAccounts: YandexMarketAccountModel[]

  @observable
  moySkladAccount: MoySkladAccountModel | null

  constructor() {
    makeObservable(this)

    this.ozonAccounts = []
    this.wbAccounts = []
    this.yandexMarketAccounts = []
    this.moySkladAccount = null
  }

  getStoreAccounts =
    (storeType: StoreIntegrationType): StoreAccountModel[] => {
      switch (storeType) {
        case IntegrationType.Ozon:
          return this.ozonAccounts

        case IntegrationType.Wildberries:
          return this.wbAccounts

        case IntegrationType.YandexMarket:
          return this.yandexMarketAccounts

        default:
          throw new UnprocessedValueError('IntegrationType', storeType)
      }
    }

  /**
   * TODO: See todos in {@link useOzonAccount} for removing boilerplate.
   */
  getOzonAccount =
    (accountId: number): OzonAccountModel =>
      this.getStoreAccount(accountId, this.ozonAccounts)

  getWbAccount =
    (accountId: number): WbAccountModel =>
      this.getStoreAccount(accountId, this.wbAccounts)

  getYandexMarketAccount =
    (accountId: number): YandexMarketAccountModel =>
      this.getStoreAccount(accountId, this.yandexMarketAccounts)

  private getStoreAccount =
    <TAccount extends StoreAccountModel>
    (accountId: number, storeAccounts: TAccount[]): TAccount =>
      required(storeAccounts.find(_ => _.id === accountId))

  replaceStoreAccount =
    (newAccount: StoreAccountModel) => {
      const storeAccounts = this.getStoreAccounts(newAccount.integrationType)
      const accountIndex = storeAccounts.findIndex(_ => _.id === newAccount.id)

      if (accountIndex < 0) {
        throw new Error(`Failed to find ${integrationTypeNames[newAccount.integrationType]} account #${newAccount.id}`)
      }

      storeAccounts[accountIndex] = newAccount
    }
}